import { zodResolver } from "@hookform/resolvers/zod";
import { UseMutationResult } from "@tanstack/react-query";
import { useState } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import {
  PaginationType,
  SetPaginationCountType,
} from "src/components/TablePaginationLimitChanger/hooks/useTablePaginationLimitChanger";
import { z } from "zod";
import { itemStockUnitFormSchema } from "../itemStockUnitSchema";
import {
  ItemStockUnit,
  ItemStockUnitFormType,
  CreateItemStockUnitFormType,
  CreateItemStockUnitResponse,
  EditItemStockUnitFormType,
  EditItemStockUnitResponse,
} from "../api/interfaces";
import { useCreateItemStockUnit, useEditItemStockUnit } from "../api/mutations";
import { useGetItemStockUnit } from "../api/queries";
import { useSetPaginationCount } from "src/hooks/useSetPaginationCount";
import { apiErrorHandler } from "src/utils/apiErrorHandler";
import { useGetAccessPermission } from "src/hooks/useGetAccessPermission";
import { AccessResource, ActionType } from "src/types/accessControl";
import { useSearchParams } from "react-router-dom";
import {
  convertStringToParams,
  getStringParam,
} from "src/utils/convertToSearchParams";

interface UseItemStockUnitHandlerProps {
  pagination: PaginationType;
  setCount: SetPaginationCountType;
}

interface HandleSubmitCreateProps {
  form: UseFormReturn<CreateItemStockUnitFormType, any, any>;
  mutation: UseMutationResult<
    CreateItemStockUnitResponse,
    Error,
    CreateItemStockUnitFormType,
    unknown
  >;
  handleClose: () => void;
}

interface HandleSubmitEditProps {
  id: string;
  form: UseFormReturn<ItemStockUnitFormType, any, undefined>;
  mutation: UseMutationResult<
    EditItemStockUnitResponse,
    Error,
    EditItemStockUnitFormType,
    unknown
  >;
  handleClose: () => void;
}

export const handleSubmitCreateItemStockUnit = async ({
  form,
  mutation,
  handleClose,
}: HandleSubmitCreateProps) => {
  const values = form.getValues();

  const payload = {
    name: values.name,
    description: values.description,
  };

  try {
    await mutation.mutateAsync(payload);

    handleClose();
  } catch (error) {
    apiErrorHandler({ error, form });
  }
};

const handleSubmitEdit = async ({
  id,
  form,
  mutation,
  handleClose,
}: HandleSubmitEditProps) => {
  const values = form.getValues();

  const payload = {
    id,
    name: values.name,
    description: values.description,
  };

  try {
    await mutation.mutateAsync(payload);

    handleClose();
  } catch (error) {
    apiErrorHandler({ error, form });
  }
};

const useItemStockUnitHandler = ({
  pagination,
  setCount,
}: UseItemStockUnitHandlerProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isAllowToCreate = useGetAccessPermission({
    resource: AccessResource["Master Data - Item Stock Unit"],
    action: ActionType["Create"],
  });
  const initialSearchByUnitStockName = getStringParam(
    searchParams,
    "unitStockName",
    ""
  );
  const [isShowFormModal, setIsShowFormModal] = useState(false);
  const [searchByUnitStockName, setSearchByUnitStockNameState] = useState(
    initialSearchByUnitStockName
  );

  const setSearchByUnitStockName = convertStringToParams(
    "unitStockName",
    setSearchByUnitStockNameState,
    setSearchParams
  );

  const [selectedData, setSelectedData] = useState<ItemStockUnit | null>(null);

  const { data, isLoading } = useGetItemStockUnit({
    ...pagination,
    unitStockName: searchByUnitStockName,
  });

  const form = useForm<z.infer<typeof itemStockUnitFormSchema>>({
    resolver: zodResolver(itemStockUnitFormSchema),
  });

  const createMutation = useCreateItemStockUnit();
  const editMutation = useEditItemStockUnit();

  const handleAdd = () => setIsShowFormModal(true);
  const handleCloseAdd = () => {
    setIsShowFormModal(false);
    form.reset({ name: "", description: "" });
  };

  const handleEdit = (data: ItemStockUnit) => {
    setIsShowFormModal(true);
    setSelectedData(data);
    form.reset({
      name: data?.name,
      description: data.description ?? "",
    });
  };
  const handleCloseEdit = () => {
    setIsShowFormModal(false);
    setSelectedData(null);
    form.reset({ name: "", description: "" });
  };

  useSetPaginationCount({ meta: data?.meta, setCount });

  const hasFilter = searchByUnitStockName !== "";

  return {
    isLoading,
    isAllowToCreate,
    searchByUnitStockName,
    itemStockUnit: data?.data,
    totalCount: data?.meta.total ?? 0,
    isShowFormModal,
    handleAdd,
    handleEdit,
    setSearchByUnitStockName,
    form,
    formType: (!selectedData ? "CREATE" : "EDIT") as "CREATE" | "EDIT",
    handleCancelFormModal: () =>
      !selectedData ? handleCloseAdd() : handleCloseEdit(),
    handleSubmitFormModal: form.handleSubmit(
      async () =>
        await (!selectedData
          ? handleSubmitCreateItemStockUnit({
              form,
              mutation: createMutation,
              handleClose: handleCloseAdd,
            })
          : handleSubmitEdit({
              id: `${selectedData?.id}`,
              form,
              mutation: editMutation,
              handleClose: handleCloseEdit,
            }))
    ),
    hasFilter,
  };
};

export default useItemStockUnitHandler;
